import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { stockDashboardApi, stockLedgerApi } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import {
  Search, SlidersHorizontal, FileText, Download, Plus,
  Boxes, Package, ArrowUpRight, ArrowDownLeft, ArrowLeftRight,
  RotateCcw, TrendingUp, TrendingDown, MapPin,
  Building2, ArrowRight, CheckCircle2, AlertTriangle, User,
  ArrowDown, ArrowUp, Activity, Warehouse, X, Loader2
} from 'lucide-react';

function StockDashboard() {
  const { currentTheme } = useTheme();
  const isDark = ['Purple', 'Black'].includes(currentTheme?.name);
  const navigate = useNavigate();

  // States for search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [timeFilter, setTimeFilter] = useState('Today'); // Today, Week, Month
  const [showFilters, setShowFilters] = useState(false);

  // Filters Panel States
  const [filterSearch, setFilterSearch] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterTxType, setFilterTxType] = useState('All');
  const [filterWarehouseStatus, setFilterWarehouseStatus] = useState('All');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterQuickFilter, setFilterQuickFilter] = useState('');

  // ── API DATA STATES ──
  const [quickStats, setQuickStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [todaySummary, setTodaySummary] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Loading & Error states
  const [statsLoading, setStatsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [warehouseLoading, setWarehouseLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Type/qty display helpers for API data ──
  const typeColorMap = {
    'Stock In': 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-500/10',
    'Transfer': 'text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-500/10',
    'Dispatched': 'text-purple-700 bg-purple-50 dark:text-purple-400 dark:bg-purple-500/10',
    'Dispatch': 'text-purple-700 bg-purple-50 dark:text-purple-400 dark:bg-purple-500/10',
    'Return': 'text-rose-700 bg-rose-50 dark:text-rose-400 dark:bg-rose-500/10',
  };
  const qtyColorMap = {
    'Stock In': 'text-emerald-600 font-bold',
    'Transfer': 'text-blue-600 font-bold',
    'Dispatched': 'text-amber-600 font-bold',
    'Dispatch': 'text-amber-600 font-bold',
    'Return': 'text-rose-600 font-bold',
  };

  // Backend activity object → UI shape mapper
  // ⚠️ Field names 16_COMPLETE_API_DOCUMENTATION.md ke response se match kar lena
  const mapActivity = (a, idx) => ({
    id: a.id || idx,
    type: a.transactionType || a.type,
    typeColor: typeColorMap[a.transactionType || a.type] || 'text-gray-600 bg-gray-50',
    product: a.productName || a.product,
    sku: a.sku || '',
    batch: a.batchNo || a.referenceId || a.batch || '',
    location: a.location || a.warehouseName || a.warehouse || '',
    qty: a.quantityDisplay || `${a.quantity > 0 ? '+' : ''}${a.quantity} units`,
    qtyColor: qtyColorMap[a.transactionType || a.type] || 'text-gray-600 font-bold',
    time: a.time || (a.createdAt ? new Date(a.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''),
    user: a.createdBy || a.userName || a.user || '',
  });

  // Backend warehouse-health object → UI shape mapper
  const mapWarehouse = (w) => ({
    code: w.code || (w.name || '').substring(0, 3).toUpperCase(),
    name: w.name || w.warehouseName,
    details: w.details || `${w.city || ''}${w.managerName ? ' - ' + w.managerName : ''}`,
    used: w.usedCapacity ?? w.used ?? 0,
    total: w.totalCapacity ?? w.total ?? 1,
    stockIn: w.stockInToday ?? w.stockIn ?? 0,
    dispatched: w.dispatchedToday ?? w.dispatched ?? 0,
    transfers: w.transfersToday ?? w.transfers ?? 0,
    returns: w.returnsToday ?? w.returns ?? 0,
    status: w.status || (w.lowStock ? 'Low Stock' : 'Operational'),
  });

  // ── FETCH: Quick Stats + Warehouse Health + Today Summary (mount pe ek baar) ──
  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setStatsLoading(true);
      setWarehouseLoading(true);
      setError(null);
      try {
        const [stats, health, today] = await Promise.all([
          stockDashboardApi.getQuickStats(),
          stockDashboardApi.getWarehouseHealth(),
          stockDashboardApi.getTodaySummary(),
        ]);
        if (cancelled) return;

        setQuickStats(stats?.data || stats);
        const whList = health?.warehouses || health?.content || health?.data || health || [];
        setWarehouses(Array.isArray(whList) ? whList.map(mapWarehouse) : []);
        setTodaySummary(today?.data || today);
        setLastUpdated(new Date());
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load dashboard data');
        console.error('Dashboard load error:', err);
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
          setWarehouseLoading(false);
        }
      }
    };

    loadDashboard();
    // Auto refresh every 2 min (header "Last updated" ke saath match)
    const interval = setInterval(loadDashboard, 2 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // ── FETCH: Recent Activity (timeFilter change pe refetch) ──
  useEffect(() => {
    let cancelled = false;

    const loadActivity = async () => {
      setActivityLoading(true);
      try {
        const res = await stockDashboardApi.getRecentActivity({
          period: timeFilter.toLowerCase(), // today | week | month
          limit: 10,
        });
        if (cancelled) return;
        const list = res?.activities || res?.content || res?.data || res || [];
        setRecentActivities(Array.isArray(list) ? list.map(mapActivity) : []);
      } catch (err) {
        if (!cancelled) console.error('Activity load error:', err);
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    };

    loadActivity();
    return () => { cancelled = true; };
  }, [timeFilter]);

  // ── Ledger Export (PDF / Excel) ──
  const [exporting, setExporting] = useState(false);
  const handleExport = async (format) => {
    try {
      setExporting(true);
      await stockLedgerApi.exportLedger({
        format,
        warehouse: filterWarehouse,
        transactionType: filterTxType,
        startDate: filterDateFrom,
        endDate: filterDateTo,
      });
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Quick Filters configuration
  const quickFiltersConfig = [
    { label: 'Stock In', activeClass: 'text-emerald-700 bg-emerald-50 border-emerald-300', inactiveClass: 'text-emerald-600 border-gray-200 hover:bg-emerald-50/10' },
    { label: 'Transfer', activeClass: 'text-blue-700 bg-blue-50 border-blue-300', inactiveClass: 'text-blue-600 border-gray-200 hover:bg-blue-50/10' },
    { label: 'Dispatch', activeClass: 'text-amber-700 bg-amber-50 border-amber-300', inactiveClass: 'text-amber-600 border-gray-200 hover:bg-amber-50/10' },
    { label: 'Return', activeClass: 'text-rose-700 bg-rose-50 border-rose-300', inactiveClass: 'text-rose-600 border-gray-200 hover:bg-rose-50/10' }
  ];

  const getTypeBadgeIcon = (type) => {
    switch (type) {
      case 'Stock In': return <ArrowDown className="w-3.5 h-3.5 mr-1 flex-shrink-0" />;
      case 'Transfer': return <ArrowLeftRight className="w-3.5 h-3.5 mr-1 flex-shrink-0" />;
      case 'Dispatched':
      case 'Dispatch': return <ArrowUp className="w-3.5 h-3.5 mr-1 flex-shrink-0" />;
      case 'Return': return <RotateCcw className="w-3.5 h-3.5 mr-1 flex-shrink-0" />;
      default: return null;
    }
  };

  const renderQty = (qty, qtyColor) => {
    const parts = String(qty).split(' ');
    const number = parts[0];
    const unit = parts[1] || 'units';
    return (
      <span className={qtyColor}>
        {number} <span className="text-gray-400 dark:text-gray-500 font-normal text-xs">{unit}</span>
      </span>
    );
  };

  // Stat value helper — loading me dash dikhao
  const stat = (value) => {
    if (statsLoading) return '—';
    return (value ?? 0).toLocaleString('en-IN');
  };

  // "Last updated X mins ago" text
  const lastUpdatedText = () => {
    if (!lastUpdated) return 'Loading...';
    const mins = Math.round((Date.now() - lastUpdated.getTime()) / 60000);
    return mins < 1 ? 'Last updated just now' : `Last updated ${mins} min${mins > 1 ? 's' : ''} ago`;
  };

  // Filter Recent Activities by Search Query (client-side, loaded data pe)
  const filteredActivities = recentActivities.filter(activity =>
    (activity.product || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (activity.sku || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (activity.location || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Today's summary bar widths (max ke relative)
  const summaryBar = (value, max) => {
    if (!max || !value) return '0%';
    return `${Math.min(100, Math.round((value / max) * 100))}%`;
  };
  const maxUnits = Math.max(
    todaySummary?.stockReceived ?? 0,
    todaySummary?.dispatched ?? 0,
    1
  );

  return (
    <div className={`p-6 space-y-6 min-h-screen ${isDark ? 'text-white' : 'text-gray-800'}`}>

      {/* ── ERROR BANNER ── */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => window.location.reload()}
            className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── HEADER ── */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Inventory Dashboard
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            All warehouses - {lastUpdatedText()}
          </p>
        </div>

        {/* Header Actions */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search product, SKU or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 pr-4 py-2 w-64 md:w-80 rounded-xl border text-sm focus:outline-none transition-all ${
                isDark
                  ? 'bg-slate-900/60 border-slate-800 focus:border-emerald-500'
                  : 'bg-white border-gray-200 focus:border-emerald-500'
              }`}
            />
            <Search className={`absolute left-3 top-2.5 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>

          {/* Filter Trigger Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
              showFilters
                ? 'bg-emerald-50 border-emerald-350 text-emerald-600'
                : isDark
                  ? 'bg-slate-900 border-slate-800 hover:bg-slate-850'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>

          {/* Export Buttons */}
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 ${
              isDark ? 'bg-slate-900 border-slate-800 hover:bg-slate-800' : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin text-rose-500" /> : <FileText className="w-4 h-4 text-rose-500" />}
            PDF
          </button>

          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 ${
              isDark ? 'bg-slate-900 border-slate-800 hover:bg-slate-800' : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin text-emerald-500" /> : <Download className="w-4 h-4 text-emerald-500" />}
            Export Excel
          </button>

          {/* New Stock In Button */}
          <button
            onClick={() => navigate('/stock-management/stock-in')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all shadow-lg hover:shadow-emerald-500/20 cursor-pointer"
            style={{ backgroundColor: currentTheme.color }}
          >
            <Plus className="w-4 h-4" />
            New Stock In
          </button>
        </div>
      </div>

      {/* ── COLLAPSIBLE FILTERS PANEL ── */}
      {showFilters && (
        <div className={`p-6 rounded-2xl border transition-all duration-300 relative ${
          isDark ? 'bg-slate-900/60 border-slate-800 shadow-2xl' : 'bg-white border-gray-200 shadow-sm'
        }`}>
          {/* Close X Button */}
          <button
            onClick={() => setShowFilters(false)}
            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            title="Close filters"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-5">
            {/* Search */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Product, user, SKU..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className={`pl-9 pr-4 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                    isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
                  }`}
                />
              </div>
            </div>

            {/* Warehouse — API se loaded warehouses */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Warehouse</label>
              <select
                value={filterWarehouse}
                onChange={(e) => setFilterWarehouse(e.target.value)}
                className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
                }`}
              >
                <option value="All">Please Select</option>
                {warehouses.map(wh => (
                  <option key={wh.code} value={wh.name}>{wh.name}</option>
                ))}
              </select>
            </div>

            {/* Transaction Type */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Transaction Type</label>
              <select
                value={filterTxType}
                onChange={(e) => setFilterTxType(e.target.value)}
                className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
                }`}
              >
                <option value="All">Please Select</option>
                <option value="Stock In">Stock In</option>
                <option value="Transfer">Transfer</option>
                <option value="Dispatch">Dispatch</option>
                <option value="Return">Return</option>
              </select>
            </div>

            {/* Warehouse Status */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Warehouse Status</label>
              <select
                value={filterWarehouseStatus}
                onChange={(e) => setFilterWarehouseStatus(e.target.value)}
                className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
                }`}
              >
                <option value="All">Please Select</option>
                <option value="Operational">Operational</option>
                <option value="Low Stock">Low Stock</option>
                <option value="Restocking Needed">Restocking Needed</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
            {/* Date Range */}
            <div className="space-y-1.5 md:col-span-2">
              <DateRangeFilter
                label="Date Range (Start – End)"
                showPresets={false}
                startDate={filterDateFrom}
                endDate={filterDateTo}
                onChange={({ startDate, endDate }) => {
                  setFilterDateFrom(startDate);
                  setFilterDateTo(endDate);
                }}
              />
            </div>

            {/* Quick Filter */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Quick Filter</label>
              <div className="flex flex-wrap gap-2.5">
                {quickFiltersConfig.map(f => {
                  const isActive = filterQuickFilter === f.label;
                  return (
                    <button
                      key={f.label}
                      type="button"
                      onClick={() => setFilterQuickFilter(isActive ? '' : f.label)}
                      className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer ${
                        isActive ? f.activeClass : f.inactiveClass + ' bg-white'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Reset Filters button */}
          <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-gray-150 dark:border-slate-800/50">
            <button
              onClick={() => {
                setFilterSearch('');
                setFilterWarehouse('All');
                setFilterTxType('All');
                setFilterWarehouseStatus('All');
                setFilterDateFrom('');
                setFilterDateTo('');
                setFilterQuickFilter('');
              }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                isDark ? 'bg-slate-955 border-slate-800 text-slate-400 hover:text-white' : 'bg-white border-gray-200 text-gray-650 hover:bg-gray-50'
              }`}
            >
              Reset Filters
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white transition-all shadow-md cursor-pointer"
              style={{ backgroundColor: currentTheme.color }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* ── STATS GRID (API: quick-stats) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: Total Products */}
        <div className={`p-4 rounded-2xl border transition-all ${
          isDark
            ? 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60'
            : 'bg-slate-50/40 border-slate-200 hover:shadow-md shadow-sm'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 dark:bg-slate-800/80 rounded-full flex-shrink-0 text-slate-600 dark:text-slate-400" style={{ backgroundColor: isDark ? 'rgba(51, 65, 85, 0.4)' : 'rgba(71, 85, 105, 0.1)' }}>
              <Boxes className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end text-[10px] font-bold text-emerald-600">
              <span className="flex items-center text-xs font-black">
                <TrendingUp className="w-3.5 h-3.5 mr-0.5" />
                {statsLoading ? '—' : `+${quickStats?.productsAddedThisMonth ?? 0}`}
              </span>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">vs last month</span>
            </div>
          </div>
          <p className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat(quickStats?.totalProducts)}</p>
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">Total Products</p>
        </div>

        {/* Card 2: Available Stock */}
        <div className={`p-4 rounded-2xl border transition-all ${
          isDark
            ? 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60'
            : 'bg-blue-50/40 border-blue-200 hover:shadow-md shadow-sm'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 dark:bg-blue-950/40 rounded-full flex-shrink-0 text-blue-600 dark:text-blue-405" style={{ backgroundColor: isDark ? 'rgba(30, 58, 138, 0.4)' : 'rgba(37, 99, 235, 0.1)' }}>
              <Warehouse className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end text-[10px] font-bold text-emerald-600">
              <span className="flex items-center text-xs font-black">
                <TrendingUp className="w-3.5 h-3.5 mr-0.5" />
                {statsLoading ? '—' : `+${quickStats?.unitsAddedToday ?? 0}`}
              </span>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">units added today</span>
            </div>
          </div>
          <p className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat(quickStats?.availableStock)}</p>
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">Available Stock</p>
        </div>

        {/* Card 3: Stock In Today */}
        <div className={`p-4 rounded-2xl border transition-all ${
          isDark
            ? 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60'
            : 'bg-emerald-50/40 border-emerald-200 hover:shadow-md shadow-sm'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 dark:bg-emerald-950/40 rounded-full flex-shrink-0 text-emerald-600 dark:text-emerald-400" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
              <ArrowDown className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end text-[10px] font-bold text-emerald-600">
              <span className="flex items-center text-xs font-black">
                <TrendingUp className="w-3.5 h-3.5 mr-0.5" />
                {statsLoading ? '—' : `${quickStats?.stockInChangePercent >= 0 ? '+' : ''}${quickStats?.stockInChangePercent ?? 0}%`}
              </span>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">vs yesterday</span>
            </div>
          </div>
          <p className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat(quickStats?.stockInToday)}</p>
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">Stock In Today</p>
        </div>

        {/* Card 4: Dispatched Today */}
        <div className={`p-4 rounded-2xl border transition-all ${
          isDark
            ? 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60'
            : 'bg-purple-50/40 border-purple-200 hover:shadow-md shadow-sm'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 dark:bg-purple-950/40 rounded-full flex-shrink-0 text-purple-600 dark:text-purple-400" style={{ backgroundColor: isDark ? 'rgba(88, 28, 135, 0.4)' : 'rgba(147, 51, 234, 0.1)' }}>
              <ArrowUp className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end text-[10px] font-bold text-rose-600">
              <span className="flex items-center text-xs font-black">
                <TrendingDown className="w-3.5 h-3.5 mr-0.5" />
                {statsLoading ? '—' : `${quickStats?.dispatchedChangePercent >= 0 ? '+' : ''}${quickStats?.dispatchedChangePercent ?? 0}%`}
              </span>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">vs yesterday</span>
            </div>
          </div>
          <p className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat(quickStats?.dispatchedToday)}</p>
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">Dispatched Today</p>
        </div>

        {/* Card 5: Pending Transfers */}
        <div className={`p-4 rounded-2xl border transition-all ${
          isDark
            ? 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60'
            : 'bg-amber-50/40 border-amber-200 hover:shadow-md shadow-sm'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 dark:bg-amber-950/40 rounded-full flex-shrink-0 text-amber-600 dark:text-amber-400" style={{ backgroundColor: isDark ? 'rgba(120, 53, 4, 0.4)' : 'rgba(217, 119, 6, 0.1)' }}>
              <ArrowLeftRight className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end text-[10px] font-bold text-rose-600">
              <span className="flex items-center text-xs font-black">
                <TrendingDown className="w-3.5 h-3.5 mr-0.5" />
                {statsLoading ? '—' : `+${quickStats?.transfersSinceMorning ?? 0}`}
              </span>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">since morning</span>
            </div>
          </div>
          <p className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat(quickStats?.pendingTransfers)}</p>
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">Pending Transfers</p>
        </div>

        {/* Card 6: Returns Today */}
        <div className={`p-4 rounded-2xl border transition-all ${
          isDark
            ? 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60'
            : 'bg-rose-50/40 border-rose-200 hover:shadow-md shadow-sm'
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="p-2.5 dark:bg-rose-950/40 rounded-full flex-shrink-0 text-rose-600 dark:text-rose-400" style={{ backgroundColor: isDark ? 'rgba(159, 18, 57, 0.4)' : 'rgba(225, 29, 72, 0.1)' }}>
              <RotateCcw className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end text-[10px] font-bold text-rose-600">
              <span className="flex items-center text-xs font-black">
                <TrendingDown className="w-3.5 h-3.5 mr-0.5" />
                {statsLoading ? '—' : `+${quickStats?.unitsReturnedToday ?? 0}`}
              </span>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 font-medium mt-0.5">units returned</span>
            </div>
          </div>
          <p className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat(quickStats?.returnsToday)}</p>
          <p className="text-xs font-bold text-gray-555 dark:text-gray-400 uppercase tracking-wider mt-1">Returns Today</p>
        </div>
      </div>

      {/* ── RECENT ACTIVITY (API: recent-activity) ── */}
      <div className={`border rounded-2xl p-6 transition-all ${
        isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-100 shadow-sm'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`text-lg font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Recent Activity
              </h3>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Stock movements across all warehouses
              </p>
            </div>
          </div>

          {/* Timeframe Buttons — API refetch trigger */}
          <div className={`flex rounded-xl p-1 text-xs font-bold ${isDark ? 'bg-slate-900' : 'bg-gray-100'}`}>
            {['Today', 'Week', 'Month'].map(tab => (
              <button
                key={tab}
                onClick={() => setTimeFilter(tab)}
                className={`px-4 py-1.5 rounded-lg transition-all ${
                  timeFilter === tab
                    ? (isDark ? 'bg-slate-850 text-white shadow' : 'bg-emerald-800 text-white shadow')
                    : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800')
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className={`border-b text-xs font-bold uppercase tracking-wider ${isDark ? 'border-slate-800 text-gray-400' : 'border-gray-100 text-gray-400'}`}>
                <th className="pb-3">Type</th>
                <th className="pb-3">Product</th>
                <th className="pb-3">Location</th>
                <th className="pb-3">Qty</th>
                <th className="pb-3">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-150 dark:divide-slate-800/40">
              {activityLoading ? (
                <tr>
                  <td colSpan="5" className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                    <span className="text-gray-400 dark:text-gray-500 text-sm">Loading activity...</span>
                  </td>
                </tr>
              ) : filteredActivities.length > 0 ? (
                filteredActivities.map(activity => (
                  <tr key={activity.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/20 transition-colors">
                    <td className="py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${activity.typeColor}`}>
                        {getTypeBadgeIcon(activity.type)}
                        {activity.type}
                      </span>
                    </td>
                    <td className="py-4">
                      <div>
                        <p className={`font-black text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{activity.product}</p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">
                          {activity.sku}  ·  {activity.batch}
                        </p>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-1.5 text-sm font-semibold">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        {activity.location}
                      </div>
                    </td>
                    <td className="py-4 text-sm">
                      {renderQty(activity.qty, activity.qtyColor)}
                    </td>
                    <td className="py-4">
                      <div>
                        <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{activity.time}</p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500">{activity.user}</p>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                    No matching activity found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* View All activity log link */}
        <div className="mt-5 text-right">
          <button
            onClick={() => navigate('/stock-management/ledger')}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors uppercase tracking-wider cursor-pointer"
          >
            View full activity log
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── WAREHOUSE SUMMARY (API: warehouse-health) ── */}
      <div className={`border rounded-2xl p-6 transition-all ${
        isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-100 shadow-sm'
      }`}>
        <div className="mb-6">
          <h3 className={`text-lg font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Warehouse Summary
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Today's movement by localities
          </p>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className={`border-b text-xs font-bold uppercase tracking-wider ${isDark ? 'border-slate-800 text-gray-400' : 'border-gray-100 text-gray-400'}`}>
                <th className="pb-3">Warehouse</th>
                <th className="pb-3">Utilisation</th>
                <th className="pb-3 text-center">Stock In</th>
                <th className="pb-3 text-center">Dispatched</th>
                <th className="pb-3 text-center">Transfers</th>
                <th className="pb-3 text-center">Returns</th>
                <th className="pb-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-150 dark:divide-slate-800/40">
              {warehouseLoading ? (
                <tr>
                  <td colSpan="7" className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                    <span className="text-gray-400 dark:text-gray-500 text-sm">Loading warehouses...</span>
                  </td>
                </tr>
              ) : warehouses.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                    No warehouses found
                  </td>
                </tr>
              ) : (
                warehouses
                  .filter(wh => filterWarehouseStatus === 'All' || wh.status === filterWarehouseStatus)
                  .map(warehouse => {
                    const percent = Math.round((warehouse.used / warehouse.total) * 100);
                    return (
                      <tr key={warehouse.code} className="hover:bg-slate-50 dark:hover:bg-slate-900/20 transition-colors">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs border ${
                              isDark ? 'bg-slate-900 border-slate-800 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'
                            }`}>
                              {warehouse.code}
                            </div>
                            <div>
                              <p className={`font-black text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{warehouse.name}</p>
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{warehouse.details}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4">
                          <div className="w-48">
                            <div className="flex items-center justify-between text-xs font-bold mb-1">
                              <span>{warehouse.used.toLocaleString()} / {warehouse.total.toLocaleString()}</span>
                              <span className="text-gray-400">{percent}%</span>
                            </div>
                            <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                              <div
                                className={`h-full rounded-full transition-all ${
                                  percent < 50 ? 'bg-rose-500' : percent < 75 ? 'bg-blue-500' : 'bg-emerald-500'
                                }`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-center font-bold text-sm text-emerald-600 dark:text-emerald-400">
                          +{warehouse.stockIn}
                        </td>
                        <td className="py-4 text-center font-bold text-sm text-amber-600 dark:text-amber-500">
                          -{warehouse.dispatched}
                        </td>
                        <td className="py-4 text-center font-semibold text-sm text-blue-600 dark:text-blue-400">
                          {warehouse.transfers}
                        </td>
                        <td className="py-4 text-center font-semibold text-sm text-rose-600 dark:text-rose-400">
                          {warehouse.returns}
                        </td>
                        <td className="py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                              warehouse.status === 'Operational'
                                ? 'text-emerald-700 dark:text-emerald-400'
                                : 'text-rose-700 dark:text-rose-400'
                            }`}
                            style={{
                              backgroundColor: isDark
                                ? (warehouse.status === 'Operational' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(244, 63, 94, 0.2)')
                                : (warehouse.status === 'Operational' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(244, 63, 94, 0.1)')
                            }}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              warehouse.status === 'Operational' ? 'bg-emerald-500' : 'bg-rose-500'
                            }`} />
                            {warehouse.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── QUICK ACTIONS ── */}
      <div className={`border rounded-2xl p-6 transition-all ${
        isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-100 shadow-sm'
      }`}>
        <div className="mb-6">
          <h3 className={`text-lg font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Quick Actions
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Common inventory tasks
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Action 1: Stock In */}
          <div
            onClick={() => navigate('/stock-management/stock-in')}
            className="group cursor-pointer relative p-5 rounded-2xl border border-emerald-100 dark:border-emerald-950/20 bg-emerald-50/40 dark:bg-emerald-950/10 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-all hover:-translate-y-0.5"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: isDark ? 'rgba(6, 78, 59, 0.4)' : 'rgba(5, 150, 105, 0.1)' }}>
              <Building2 className="w-5 h-5" />
            </div>
            <h4 className="font-black text-sm uppercase tracking-wide text-emerald-900 dark:text-emerald-400 mb-1">Stock In</h4>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-500/80">Record incoming stock</p>
          </div>

          {/* Action 2: Transfer Stock — pending count API se */}
          <div
            onClick={() => navigate('/stock-management/transfer')}
            className="group cursor-pointer relative p-5 rounded-2xl border border-blue-100 dark:border-blue-950/20 bg-blue-50/40 dark:bg-blue-950/10 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all hover:-translate-y-0.5"
          >
            {!statsLoading && (quickStats?.pendingTransfers ?? 0) > 0 && (
              <div className="absolute top-4 right-4 bg-amber-500 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                {quickStats.pendingTransfers} pending
              </div>
            )}
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: isDark ? 'rgba(30, 58, 138, 0.4)' : 'rgba(37, 99, 235, 0.1)' }}>
              <ArrowLeftRight className="w-5 h-5" />
            </div>
            <h4 className="font-black text-sm uppercase tracking-wide text-blue-900 dark:text-blue-400 mb-1">Transfer Stock</h4>
            <p className="text-xs text-blue-700/80 dark:text-blue-500/80">Move between warehouses</p>
          </div>

          {/* Action 3: Receive Return */}
          <div
            onClick={() => navigate('/stock-management/returns')}
            className="group cursor-pointer relative p-5 rounded-2xl border border-rose-100 dark:border-rose-950/20 bg-rose-50/40 dark:bg-rose-950/10 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-all hover:-translate-y-0.5"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-rose-600 dark:text-rose-455 mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: isDark ? 'rgba(159, 18, 57, 0.4)' : 'rgba(225, 29, 72, 0.1)' }}>
              <RotateCcw className="w-5 h-5" />
            </div>
            <h4 className="font-black text-sm uppercase tracking-wide text-rose-900 dark:text-rose-400 mb-1">Receive Return</h4>
            <p className="text-xs text-rose-700/80 dark:text-rose-500/80">Log returned items</p>
          </div>

          {/* Action 4: Stock Ledger */}
          <div
            onClick={() => navigate('/stock-management/ledger')}
            className="group cursor-pointer relative p-5 rounded-2xl border border-purple-100 dark:border-purple-950/20 bg-purple-50/40 dark:bg-purple-950/10 hover:bg-purple-50 dark:hover:bg-purple-950/20 transition-all hover:-translate-y-0.5"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-purple-600 dark:text-purple-400 mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: isDark ? 'rgba(88, 28, 135, 0.4)' : 'rgba(147, 51, 234, 0.1)' }}>
              <FileText className="w-5 h-5" />
            </div>
            <h4 className="font-black text-sm uppercase tracking-wide text-purple-900 dark:text-purple-400 mb-1">Stock Ledger</h4>
            <p className="text-xs text-purple-700/80 dark:text-purple-500/80">Full movement history</p>
          </div>
        </div>
      </div>

      {/* ── TODAY'S SUMMARY (API: today/summary) ── */}
      <div className={`border rounded-2xl p-6 transition-all ${
        isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-100 shadow-sm'
      }`}>
        <div className="mb-6">
          <h3 className={`text-lg font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Today's Summary
          </h3>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Movement breakdown
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Summary Side */}
          <div className="space-y-5">
            {/* Stock Received */}
            <div>
              <div className="flex justify-between items-center text-sm font-semibold mb-2">
                <span className={isDark ? 'text-gray-300' : 'text-gray-650'}>Stock Received</span>
                <span className="font-black text-gray-900 dark:text-white">
                  {(todaySummary?.stockReceived ?? 0).toLocaleString('en-IN')} units
                </span>
              </div>
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-850' : 'bg-gray-100'}`}>
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: summaryBar(todaySummary?.stockReceived, maxUnits) }} />
              </div>
            </div>

            {/* Dispatched */}
            <div>
              <div className="flex justify-between items-center text-sm font-semibold mb-2">
                <span className={isDark ? 'text-gray-300' : 'text-gray-650'}>Dispatched</span>
                <span className="font-black text-gray-900 dark:text-white">
                  {(todaySummary?.dispatched ?? 0).toLocaleString('en-IN')} units
                </span>
              </div>
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-850' : 'bg-gray-100'}`}>
                <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: summaryBar(todaySummary?.dispatched, maxUnits) }} />
              </div>
            </div>
          </div>

          {/* Right Summary Side */}
          <div className="space-y-5">
            {/* Transfers */}
            <div>
              <div className="flex justify-between items-center text-sm font-semibold mb-2">
                <span className={isDark ? 'text-gray-300' : 'text-gray-650'}>Transfers</span>
                <span className="font-black text-gray-900 dark:text-white">
                  {todaySummary?.transferOrders ?? 0} orders
                </span>
              </div>
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-850' : 'bg-gray-100'}`}>
                <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: summaryBar(todaySummary?.transferOrders, Math.max(todaySummary?.transferOrders ?? 0, 10)) }} />
              </div>
            </div>

            {/* Returns */}
            <div>
              <div className="flex justify-between items-center text-sm font-semibold mb-2">
                <span className={isDark ? 'text-gray-300' : 'text-gray-650'}>Returns</span>
                <span className="font-black text-gray-900 dark:text-white">
                  {(todaySummary?.returns ?? 0).toLocaleString('en-IN')} units
                </span>
              </div>
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-850' : 'bg-gray-100'}`}>
                <div className="h-full rounded-full bg-rose-500 transition-all" style={{ width: summaryBar(todaySummary?.returns, maxUnits) }} />
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default StockDashboard;